import { parsePolicyExpiryIsoToMillis } from '@angular-workspace/services';

const SUCCESS = 'urn:dx:apdServerPanel:success';
const PENDING = 'urn:dx:apdServerPanel:access-pending';
const NO_ACCESS = 'urn:dx:apdServerPanel:no-access';

/**
 * Unwrap Angular `HttpErrorResponse` (uses `.error` as body) or return a direct API body.
 */
function extractBody(source: unknown): unknown {
  if (source == null) return undefined;
  if (typeof source === 'object' && source !== null && 'error' in source) {
    const err = source as { error?: unknown };
    return err.error;
  }
  return source;
}

function inferPendingFromText(combined: string): boolean {
  const s = combined.toLowerCase();
  return (
    s.includes('request sent') ||
    s.includes('request has been sent') ||
    s.includes('request has been submitted') ||
    s.includes('access request is pending') ||
    s.includes('awaiting approval')
  );
}

function inferDeniedFromText(combined: string): boolean {
  const s = combined.toLowerCase();
  return (
    s.includes('no access') ||
    s.includes('access denied') ||
    s.includes('does not have access') ||
    s.includes('not authorized') ||
    s.includes('not granted')
  );
}

/**
 * Normalize ACL-APD `has_access` success body or error body to `{ type }` values used across MahaAgX / IUDX.
 */
export function normalizeAclAccessStatus(source: unknown): { type?: string } {
  let body = extractBody(source);

  if (typeof body === 'string') {
    const t = body.trim();
    if (!t) return {};
    try {
      body = JSON.parse(t);
    } catch {
      return inferPendingFromText(t) ? { type: PENDING } : {};
    }
  }

  if (body == null || typeof body !== 'object') {
    return {};
  }

  const o = body as Record<string, unknown>;
  const nested =
    typeof o['result'] === 'object' && o['result'] !== null
      ? (o['result'] as Record<string, unknown>)
      : typeof o['data'] === 'object' && o['data'] !== null
        ? (o['data'] as Record<string, unknown>)
        : undefined;

  const firstResult =
    Array.isArray(o['results']) && o['results'].length > 0 && typeof o['results'][0] === 'object'
      ? (o['results'][0] as Record<string, unknown>)
      : undefined;

  const typeRaw = [o['type'], o['accessType'], nested?.['type'], firstResult?.['type']].find(
    (x): x is string => typeof x === 'string',
  );
  const typeStr = typeRaw?.trim() ?? '';

  const combinedMsg = [o['detail'], o['title'], o['message'], nested?.['detail'], nested?.['title']]
    .filter((x): x is string => typeof x === 'string')
    .join(' ');

  if (typeStr) {
    const lower = typeStr.toLowerCase();
    if (lower === SUCCESS.toLowerCase() || lower.includes('apdserverpanel:success')) {
      return { type: SUCCESS };
    }
    if (lower.includes('access-pending') || lower.includes('apdserverpanel:access-pending')) {
      return { type: PENDING };
    }
    if (lower.includes('no-access') || lower.includes('apdserverpanel:no-access')) {
      return { type: NO_ACCESS };
    }
    if (lower.startsWith('urn:')) {
      return { type: typeStr };
    }
  }

  if (inferPendingFromText(combinedMsg)) {
    return { type: PENDING };
  }

  if (inferDeniedFromText(combinedMsg)) {
    return { type: NO_ACCESS };
  }

  return {};
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }
  const looksJson =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'));
  if (!looksJson) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

/** Unwrap HttpErrorResponse / JSON string without dropping a policy-array body. */
function unwrapHasAccessPayload(source: unknown): unknown {
  if (source == null) {
    return source;
  }

  if (
    typeof source === 'object' &&
    source !== null &&
    'error' in source &&
    'status' in source &&
    typeof (source as { status?: unknown }).status === 'number'
  ) {
    return unwrapHasAccessPayload((source as { error?: unknown }).error);
  }

  return parseMaybeJson(source);
}

function isPolicyRecord(value: unknown): boolean {
  const rec = asRecord(value);
  if (!rec) {
    return false;
  }
  return (
    'policyId' in rec ||
    'policyType' in rec ||
    'constraints' in rec ||
    'expiryAt' in rec
  );
}

function collectAccessConstraintEntriesDeep(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): Record<string, unknown>[] {
  const parsed = parseMaybeJson(value);
  if (parsed == null || depth > 10) {
    return [];
  }

  if (Array.isArray(parsed)) {
    return parsed.flatMap((item) => collectAccessConstraintEntriesDeep(item, depth + 1, seen));
  }

  const rec = asRecord(parsed);
  if (!rec || seen.has(rec)) {
    return [];
  }
  seen.add(rec);

  const entries: Record<string, unknown>[] = [];
  const pushAccessValue = (accessValue: unknown): void => {
    const accessParsed = parseMaybeJson(accessValue);
    if (Array.isArray(accessParsed)) {
      for (const item of accessParsed) {
        const entry = asRecord(parseMaybeJson(item));
        if (entry) {
          entries.push(entry);
        }
      }
      return;
    }
    const single = asRecord(accessParsed);
    if (single && ('accessType' in single || 'expiry' in single || 'expiryAt' in single)) {
      entries.push(single);
    }
  };

  if ('access' in rec) {
    pushAccessValue(rec['access']);
  }

  const constraints = parseMaybeJson(rec['constraints']);
  const constraintsRec = asRecord(constraints);
  if (constraintsRec && 'access' in constraintsRec) {
    pushAccessValue(constraintsRec['access']);
  }

  for (const child of Object.values(rec)) {
    if (child && typeof child === 'object') {
      entries.push(...collectAccessConstraintEntriesDeep(child, depth + 1, seen));
    } else if (typeof child === 'string') {
      const nested = parseMaybeJson(child);
      if (nested !== child) {
        entries.push(...collectAccessConstraintEntriesDeep(nested, depth + 1, seen));
      }
    }
  }

  return entries;
}

function hasPolicyListShape(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): boolean {
  const parsed = parseMaybeJson(value);
  if (parsed == null || depth > 10) {
    return false;
  }
  if (Array.isArray(parsed)) {
    return parsed.length === 0 || parsed.some((item) => hasPolicyListShape(item, depth + 1, seen));
  }
  const rec = asRecord(parsed);
  if (!rec || seen.has(rec)) {
    return false;
  }
  seen.add(rec);
  if (isPolicyRecord(rec) || ('access' in rec && rec['access'] != null)) {
    return true;
  }
  return Object.values(rec).some((child) => {
    if (child && typeof child === 'object') {
      return hasPolicyListShape(child, depth + 1, seen);
    }
    if (typeof child === 'string') {
      const nested = parseMaybeJson(child);
      return nested !== child && hasPolicyListShape(nested, depth + 1, seen);
    }
    return false;
  });
}

function readIsoExpiryField(entry: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(entry, key)) {
      continue;
    }
    const raw = entry[key];
    if (raw == null) {
      continue;
    }
    const value = String(raw).trim();
    if (value) {
      return value;
    }
  }
  return null;
}

function readAccessExpiry(entry: Record<string, unknown>): string | null {
  return readIsoExpiryField(entry, ['expiry', 'expiryAt', 'expiry_at']);
}

function collectPolicyExpiryAtDeep(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): string[] {
  const parsed = parseMaybeJson(value);
  if (parsed == null || depth > 10) {
    return [];
  }
  if (Array.isArray(parsed)) {
    return parsed.flatMap((item) => collectPolicyExpiryAtDeep(item, depth + 1, seen));
  }
  const rec = asRecord(parsed);
  if (!rec || seen.has(rec)) {
    return [];
  }
  seen.add(rec);

  const found: string[] = [];
  if (isPolicyRecord(rec)) {
    const expiryAt = readIsoExpiryField(rec, ['expiryAt', 'expiry_at', 'expiresAt']);
    if (expiryAt) {
      found.push(expiryAt);
    }
  }
  for (const child of Object.values(rec)) {
    if (child && typeof child === 'object') {
      found.push(...collectPolicyExpiryAtDeep(child, depth + 1, seen));
    } else if (typeof child === 'string') {
      const nested = parseMaybeJson(child);
      if (nested !== child) {
        found.push(...collectPolicyExpiryAtDeep(nested, depth + 1, seen));
      }
    }
  }
  return found;
}

/** Timezone-less policy expiry is IST wall-clock (`YYYY-MM-DDTHH:mm[:ss]`). */
function parseAccessExpiryToMillis(iso: string): number {
  const trimmed = String(iso ?? '').trim();
  if (!trimmed) {
    return 0;
  }
  if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return parsePolicyExpiryIsoToMillis(trimmed);
  }
  const normalized = trimmed.replace(' ', 'T');
  const withSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)
    ? `${normalized}:00`
    : normalized;
  const istParsed = new Date(`${withSeconds}+05:30`);
  if (!Number.isNaN(istParsed.getTime())) {
    return istParsed.getTime();
  }
  return parsePolicyExpiryIsoToMillis(trimmed);
}

/**
 * Inspects has_access policy-list payloads such as:
 * `[{ policyId, constraints: { access: [{ accessType, expiry }] }, expiryAt: "YYYY-MM-DDTHH:mm" }]`
 *
 * - `granted`: `expiryAt` or `constraints.access[].expiry` is in the future
 * - `request-access`: policy list is empty, expiry is missing, or expiry is past
 * - `unchanged`: payload is the older URN-only has_access body
 */
export function resolveHasAccessExpiryDecision(
  source: unknown,
): 'granted' | 'request-access' | 'unchanged' {
  const body = unwrapHasAccessPayload(source);
  if (!hasPolicyListShape(body)) {
    return 'unchanged';
  }

  const accessEntries = collectAccessConstraintEntriesDeep(body);
  const expiries: string[] = [];
  for (const entry of accessEntries) {
    const expiry = readAccessExpiry(entry);
    if (expiry) {
      expiries.push(expiry);
    }
  }
  expiries.push(...collectPolicyExpiryAtDeep(body));

  if (expiries.length === 0) {
    return 'request-access';
  }

  const now = Date.now();
  for (const expiry of expiries) {
    if (parseAccessExpiryToMillis(expiry) > now) {
      return 'granted';
    }
  }

  return 'request-access';
}
